/**
 * TodaysEventsCard
 * ================
 * Dashboard card showing two speed-dating tracks:
 *   1) Text Speed Dating — always available, routes to on-demand `/text-speed-dating`
 *   2) Video Speed Dating — shows the next *scheduled* video event from the events
 *      API. If none scheduled, shows a "coming soon" placeholder (no broken Join).
 *
 * The full scheduled-event grid is reachable via "View all →".
 */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Video, MessageSquare, Clock, ChevronRight } from "lucide-react";
import { fetchDailyEvents } from "../api/events";
import { useAuth } from "../context/AuthContext";
import { normalizeList } from "../utils/normalize";

function formatEventTime(isoString, isAllDay) {
  if (isAllDay) return "All Day";
  if (!isoString) return "TBA";
  try {
    const date = new Date(isoString);
    const now = new Date();
    const sameDay = date.toDateString() === now.toDateString();
    const timeStr = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    if (sameDay) return `Today, ${timeStr}`;
    const dayStr = date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    return `${dayStr}, ${timeStr}`;
  } catch {
    return "TBA";
  }
}

function TextRow({ onJoin }) {
  return (
    <div
      className="flex items-center justify-between gap-3 p-3 rounded-xl border bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 transition"
      data-testid="dashboard-event-text-row"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs font-medium flex-shrink-0 bg-blue-500/20 text-blue-600 dark:text-blue-300 border-blue-500/30">
          <MessageSquare className="w-4 h-4" />
          <span>Text</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-gray-900 dark:text-white font-medium">Available now</div>
          <div className="text-xs text-gray-500 dark:text-white/50">10-minute conversations · 6 rounds</div>
        </div>
      </div>
      <button
        onClick={onJoin}
        className="px-3 py-1.5 rounded-lg text-xs font-semibold transition flex-shrink-0 bg-rose-500 text-white hover:bg-rose-600"
        data-testid="dashboard-event-text-join"
      >
        Join
      </button>
    </div>
  );
}

function VideoRow({ event, onJoin }) {
  const hasEvent = !!event;
  const isAllDay = event?.is_all_day === true;
  const time = hasEvent ? formatEventTime(event.starts_at || event.start_time_iso, isAllDay) : "Coming soon";
  const isTicketed = event?.ticket_required;

  return (
    <div
      className={`flex items-center justify-between gap-3 p-3 rounded-xl border transition ${
        isTicketed
          ? "bg-gradient-to-r from-purple-500/10 to-pink-500/10 dark:from-purple-900/30 dark:to-pink-900/30 border-purple-400/30"
          : "bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20"
      }`}
      data-testid="dashboard-event-video-row"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs font-medium flex-shrink-0 bg-pink-500/20 text-pink-600 dark:text-pink-300 border-pink-500/30">
          <Video className="w-4 h-4" />
          <span>Video</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-gray-900 dark:text-white font-medium truncate">
            {hasEvent ? (event.title?.split("—")[0].trim() || "Video Speed Dating") : "Video Speed Dating"}
          </div>
          <div className="text-xs text-gray-500 dark:text-white/50">
            {time}
            {isTicketed && <span className="ml-2 text-purple-600 dark:text-purple-300">· $9.99</span>}
          </div>
        </div>
      </div>
      {hasEvent ? (
        <button
          onClick={() => onJoin(event)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex-shrink-0 ${
            isTicketed
              ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
              : "bg-rose-500 text-white hover:bg-rose-600"
          }`}
          data-testid="dashboard-event-video-join"
        >
          {isTicketed ? "Get Ticket" : "Join"}
        </button>
      ) : (
        <span
          className="px-3 py-1.5 rounded-lg text-xs font-medium flex-shrink-0 text-gray-400 dark:text-white/40 italic"
          data-testid="dashboard-event-video-soon"
        >
          Soon
        </span>
      )}
    </div>
  );
}

export default function TodaysEventsCard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [nextVideoEvent, setNextVideoEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchDailyEvents({ range: "week" }, token);
      const all = normalizeList(data, ["events"]);
      // Find the next VIDEO event (skip text "all day" placeholders entirely)
      const video = all.find((e) => {
        const t = (e.type || e.mode || "").toLowerCase();
        return t === "video";
      });
      setNextVideoEvent(video || null);
    } catch (err) {
      console.error("[TodaysEventsCard] Error:", err);
      setNextVideoEvent(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleJoinText = () => navigate("/text-speed-dating");
  const handleJoinVideo = (event) => navigate(`/events/speed-dating/${event.id}`);
  const handleViewAll = () => navigate("/events/speed-dating");

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white/80 dark:bg-slate-900/60 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Speed Dating</h3>
            <p className="text-xs text-gray-500 dark:text-white/50">Match in minutes</p>
          </div>
        </div>

        <button
          onClick={handleViewAll}
          className="flex items-center gap-1 text-xs text-gray-500 dark:text-white/60 hover:text-gray-700 dark:hover:text-white transition"
          data-testid="dashboard-events-view-all"
        >
          View all
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Two-row layout: Text (always on) + Video (next scheduled or "Soon") */}
      <div className="space-y-2">
        <TextRow onJoin={handleJoinText} />
        {loading ? (
          <div className="h-14 rounded-xl bg-gray-100 dark:bg-white/5 animate-pulse" />
        ) : (
          <VideoRow event={nextVideoEvent} onJoin={handleJoinVideo} />
        )}
      </div>

      {/* Footer hint */}
      <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-400 dark:text-white/40">
        <Clock className="w-3 h-3" />
        <span>Text runs all day · Video events are scheduled</span>
      </div>
    </div>
  );
}
